import { useMemo, useState } from "react";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import {
  useAddUserGoalMutation,
  useDeleteUserGoalMutation,
  useUpdateUserGoalMutation,
} from "../../api/managementApi";
import GoalWizardDrawer from "./GoalWizard";
import ConfirmDeleteGoal from "./ConfirmDeleteGoal";
import { goalToInitial, toGoalRequest } from "./goalRules";

/**
 * Grup yöneticisinin bir üyenin grup hedeflerini eklemesi, düzenlemesi ve
 * silmesi (mobil goal-form'un memberId'li hâli). Yalnız grup hedefleri
 * (isCustom === false) listelenir; kişisel hedefler üyenin kendisine ait.
 * Sihirbaz ve silme onayı `elements` ile çağıranın içine konur.
 */
export default function useMemberGoalEditor({ groupId, member, refreshing = false }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [addGoal, { isLoading: adding }] = useAddUserGoalMutation();
  const [updateGoal, { isLoading: updating }] = useUpdateUserGoalMutation();
  const [deleteGoal, { isLoading: deleting }] = useDeleteUserGoalMutation();

  const [wizard, setWizard] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const goals = useMemo(
    () => (member?.goalDetail || []).filter((g) => g && g.isCustom === false),
    [member]
  );
  const targetUserId = member?.id;
  const targetName = member ? `${member.name || ""} ${member.surname || ""}`.trim() : "";
  const busy = adding || updating || deleting || Boolean(refreshing);
  const editingGoal = wizard?.mode === "edit" ? wizard.goal : null;

  const fail = (err) => toast.error(err?.message || err?.data?.message || t("manage_goals_error"));

  const handleSubmit = async (value) => {
    const body = toGoalRequest(value, editingGoal ? { label: editingGoal.label } : {});
    try {
      await (editingGoal ? updateGoal : addGoal)({ groupId, targetUserId, ...body }).unwrap();
      toast.success(t(editingGoal ? "manage_goals_updated" : "manage_goals_added"));
      setWizard(null);
    } catch (err) {
      fail(err);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteGoal({
        groupId,
        targetUserId,
        goalLabel: toDelete.label,
        activity: toDelete.activity || "okuma",
      }).unwrap();
      toast.success(t("manage_goals_deleted"));
      setToDelete(null);
      setWizard(null);
    } catch (err) {
      fail(err);
    }
  };

  const elements = (
    <>
      <GoalWizardDrawer
        open={!!wizard}
        mode={wizard?.mode || "add"}
        initial={editingGoal ? goalToInitial(editingGoal) : undefined}
        subtitle={targetName || member?.email}
        submitting={adding || updating}
        onSubmit={handleSubmit}
        onClose={() => setWizard(null)}
        onDelete={editingGoal ? () => setToDelete(editingGoal) : undefined}
      />
      <ConfirmDeleteGoal
        open={!!toDelete}
        label={toDelete?.label}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
    </>
  );

  return {
    goals,
    busy,
    targetName,
    openAdd: () => setWizard({ mode: "add" }),
    openEdit: (goal) => setWizard({ mode: "edit", goal }),
    askDelete: (goal) => setToDelete(goal),
    elements,
  };
}
